import React, { useContext, useEffect, useState, useMemo } from "react";
import { useAuthContext } from "../../contexts/AuthContext";
import { useParams } from "react-router";
import dayjs from "dayjs";

import Sidebar from "../UI/Sidebar";
import Loader from "../UI/Loader";
import ErrorMessage from "../UI/ErrorMessage";
import GanttView from "./GanttView";
import { sfQuery } from "../API";

const WebsiteTeamView = () => {
  const [stagingSites, setStagingSites] = useState([]);
  const [websiteUpdates, setWebsiteUpdates] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedUser, setSelectedUser] = useState(""); // Start empty, will be set to current user
  const [availableUsers, setAvailableUsers] = useState([]);

  const context = useAuthContext();
  const { sidebar } = useParams();

  // Set default user to current logged-in user when context is ready
  useEffect(() => {
    if (context?.ready && context?.userInfo?.userFullName && !selectedUser) {
      setSelectedUser(context.userInfo.userFullName);
    }
  }, [context?.ready, context?.userInfo?.userFullName, selectedUser]);

  // Priority options for website items (simplified)
  const priorityOptions = [
    "All",
    "Urgent",
    "High",
    "Medium-High",
    "Medium",
    "Low",
  ];

  // Status options for staging sites and website updates
  const statusOptions = [
    "All",
    "Being Built", // For staging sites
    "Low",
    "Medium",
    "Medium-High",
    "High",
    "Urgent",
  ];

  // Priority mapping for cleaner categorization
  const getPriorityCategory = (priority, isStaging = false) => {
    if (isStaging) {
      // Staging sites are always high priority
      return "High";
    }

    if (!priority) return "Medium";

    const priorityLower = priority.toLowerCase();
    if (priorityLower === "urgent") {
      return "Urgent";
    }
    if (priorityLower === "high") {
      return "High";
    }
    if (priorityLower === "medium-high") {
      return "High"; // Treat Medium-High as High for categorization
    }
    if (priorityLower === "medium") {
      return "Medium";
    }
    if (priorityLower === "low") {
      return "Low";
    }
    return "Medium"; // Default fallback
  };

  // Processed data for GanttView
  const processedData = useMemo(() => {
    if (!context?.ready) return [];

    const processed = [];

    // Process staging sites
    stagingSites.forEach((site) => {
      const dueDate = dayjs(site.Staging_Site_Due__c);
      const startDate = dueDate.subtract(14, "days"); // 2 weeks before due date

      processed.push({
        id: site.Id,
        name: site.Name,
        createdDate: startDate.format("YYYY-MM-DD"),
        expectedDate: dueDate.format("YYYY-MM-DD"),
        assignedTo:
          site.Staging_Site_Creation_Assigned_To__r?.Name || "Unassigned",
        priority: "High", // Always high for staging sites
        status: "Being Built",
        type: "staging_site",
        isCompleted: false, // Staging sites in "Being Built" status are not completed
      });
    });

    // Process website updates
    websiteUpdates.forEach((update) => {
      // Use Update_Due__c if available, otherwise fall back to Due_Date__c
      const dueDate = update.Update_Due__c || update.Due_Date__c;

      processed.push({
        id: update.Id,
        name: update.Name,
        createdDate: dayjs(update.CreatedDate).format("YYYY-MM-DD"),
        expectedDate: dayjs(dueDate).format("YYYY-MM-DD"),
        assignedTo: update.Assigned_To__r?.Name || "Unassigned",
        priority: update.Priority__c || "Medium",
        status: update.Website_Update_Status__c || "Open",
        type: "website_update",
        isCompleted: update.Website_Update_Status__c === "Completed",
      });
    });

    return processed;
  }, [stagingSites, websiteUpdates, context?.ready]);

  // Get available users for filter
  useEffect(() => {
    if (!context?.ready) return;

    const users = new Set();

    // Always include the current logged-in user, even if they have no items
    if (context?.userInfo?.userFullName) {
      users.add(context.userInfo.userFullName);
    }

    // Extract users from staging sites
    stagingSites.forEach((site) => {
      const assignedTo = site.Staging_Site_Creation_Assigned_To__r?.Name;
      if (assignedTo && assignedTo !== "Unassigned") {
        users.add(assignedTo);
      }
    });

    // Extract users from website updates
    websiteUpdates.forEach((update) => {
      const assignedTo = update.Assigned_To__r?.Name;
      if (assignedTo && assignedTo !== "Unassigned") {
        users.add(assignedTo);
      }
    });

    setAvailableUsers([...Array.from(users).sort()]);
  }, [
    stagingSites,
    websiteUpdates,
    context?.ready,
    context?.userInfo?.userFullName,
  ]);

  // Filtered data based on search and filters
  const filteredData = useMemo(() => {
    if (!context?.ready) return [];

    return processedData.filter((item) => {
      // User filter - only show items assigned to the selected user
      if (selectedUser && item.assignedTo !== selectedUser) {
        return false;
      }

      // Search filter
      if (
        searchQuery &&
        !item.name.toLowerCase().includes(searchQuery.toLowerCase())
      ) {
        return false;
      }

      // Priority filter
      if (selectedPriority !== "All") {
        const itemPriority = getPriorityCategory(
          item.priority,
          item.type === "staging_site"
        );
        if (itemPriority !== selectedPriority) {
          return false;
        }
      }

      // Status filter
      if (selectedStatus !== "All" && item.status !== selectedStatus) {
        return false;
      }

      return true;
    });
  }, [
    processedData,
    searchQuery,
    selectedPriority,
    selectedStatus,
    selectedUser,
    context?.ready,
  ]);

  // Statistics (calculated from filtered data in current view only)
  const stats = useMemo(() => {
    const total = filteredData.length;
    const completed = filteredData.filter((item) => item.isCompleted).length;
    const urgent = filteredData.filter(
      (item) =>
        getPriorityCategory(item.priority, item.type === "staging_site") ===
        "Urgent"
    ).length;
    const high = filteredData.filter(
      (item) =>
        getPriorityCategory(item.priority, item.type === "staging_site") ===
        "High"
    ).length;
    const stagingSitesCount = filteredData.filter(
      (item) => item.type === "staging_site"
    ).length;
    const updatesCount = filteredData.filter(
      (item) => item.type === "website_update"
    ).length;

    return {
      total,
      completed,
      urgent,
      high,
      stagingSites: stagingSitesCount,
      updates: updatesCount,
    };
  }, [filteredData]);

  // Fetch staging sites data
  const fetchStagingSites = async () => {
    try {
      setLoading(true);
      const sites = await sfQuery(
        "Staging_Site_Build__c",
        "Id, Name, Staging_Site_Due__c, Staging_Site_Creation_Assigned_To__r.Name",
        "Staging_Site_Status__c = 'Being Built'",
        context
      );
      setStagingSites(sites || []);
    } catch (error) {
      console.error("Error fetching staging sites:", error);
      setError(true);
    }
  };

  // Fetch website updates data
  const fetchWebsiteUpdates = async () => {
    try {
      const updates = await sfQuery(
        "Website_Update__c",
        "Id, Name, CreatedDate, Due_Date__c, Update_Due__c, Assigned_To__r.Name, Website_Update_Status__c, Priority__c",
        "CreatedDate >= LAST_N_DAYS:30",
        context
      );
      setWebsiteUpdates(updates || []);
    } catch (error) {
      console.error("Error fetching website updates:", error);
      setError(true);
    }
  };

  // Fetch all data for specific user
  useEffect(() => {
    if (!context?.ready || !selectedUser) return;

    setError(false);
    setLoading(true);

    Promise.all([fetchStagingSites(), fetchWebsiteUpdates()]).finally(() => {
      setLoading(false);
    });
  }, [context?.ready, selectedUser]);

  // Loading and error states
  if (!context?.ready) return <Loader />;
  if (loading) return <Loader />;
  if (error) return <ErrorMessage />;

  // Show user selection prompt if no user is selected
  if (!selectedUser) {
    return (
      <>
        {!sidebar && <Sidebar />}
        <div className={sidebar ? "contentWOS" : "content"}>
          <div style={{ padding: "20px", textAlign: "center" }}>
            <h1
              style={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: "2.5rem",
                fontWeight: "bold",
                marginBottom: "30px",
              }}
            >
              🌐 Website Team Management
            </h1>
            <div
              style={{
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px)",
                borderRadius: "20px",
                padding: "40px",
                maxWidth: "500px",
                margin: "0 auto",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              }}
            >
              <p
                style={{
                  fontSize: "1.2rem",
                  marginBottom: "20px",
                  color: "#374151",
                }}
              >
                Please select a user to view their website items
              </p>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                style={{
                  padding: "12px 40px 12px 20px",
                  fontSize: "16px",
                  borderRadius: "10px",
                  border: "2px solid #d1d5db",
                  minWidth: "200px",
                  background: "white",
                  backgroundImage:
                    'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>\')',
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  backgroundSize: "12px",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                }}
              >
                <option value="">Select User</option>
                {availableUsers.map((user) => (
                  <option key={user} value={user}>
                    {user}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!sidebar && <Sidebar />}
      <div className={sidebar ? "contentWOS" : "content"}>
        <div style={{ padding: "20px" }}>
          <h1
            style={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: "2.5rem",
              fontWeight: "bold",
              marginBottom: "30px",
            }}
          >
            🌐 Website Team Management
          </h1>

          {/* Statistics Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "20px",
              marginBottom: "30px",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                color: "white",
                padding: "20px",
                borderRadius: "15px",
                textAlign: "center",
                boxShadow: "0 8px 32px rgba(16, 185, 129, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <div style={{ fontSize: "2rem", fontWeight: "bold" }}>
                {stats.total}
              </div>
              <div style={{ fontSize: "0.9rem", opacity: 0.9 }}>
                Total Items
              </div>
            </div>

            <div
              style={{
                background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                color: "white",
                padding: "20px",
                borderRadius: "15px",
                textAlign: "center",
                boxShadow: "0 8px 32px rgba(59, 130, 246, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <div style={{ fontSize: "2rem", fontWeight: "bold" }}>
                {stats.stagingSites}
              </div>
              <div style={{ fontSize: "0.9rem", opacity: 0.9 }}>
                Staging Sites
              </div>
            </div>

            <div
              style={{
                background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                color: "white",
                padding: "20px",
                borderRadius: "15px",
                textAlign: "center",
                boxShadow: "0 8px 32px rgba(139, 92, 246, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <div style={{ fontSize: "2rem", fontWeight: "bold" }}>
                {stats.updates}
              </div>
              <div style={{ fontSize: "0.9rem", opacity: 0.9 }}>
                Website Updates
              </div>
            </div>

            <div
              style={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                color: "white",
                padding: "20px",
                borderRadius: "15px",
                textAlign: "center",
                boxShadow: "0 8px 32px rgba(245, 158, 11, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <div style={{ fontSize: "2rem", fontWeight: "bold" }}>
                {stats.high}
              </div>
              <div style={{ fontSize: "0.9rem", opacity: 0.9 }}>
                High Priority
              </div>
            </div>

            <div
              style={{
                background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                color: "white",
                padding: "20px",
                borderRadius: "15px",
                textAlign: "center",
                boxShadow: "0 8px 32px rgba(34, 197, 94, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <div style={{ fontSize: "2rem", fontWeight: "bold" }}>
                {stats.completed}
              </div>
              <div style={{ fontSize: "0.9rem", opacity: 0.9 }}>Completed</div>
            </div>
          </div>

          {/* Filters */}
          <div
            style={{
              display: "flex",
              gap: "20px",
              marginBottom: "30px",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <input
              type="text"
              placeholder="🔍 Search website items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: "12px 20px",
                borderRadius: "10px",
                border: "2px solid #e5e7eb",
                fontSize: "14px",
                background: "white",
                minWidth: "250px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            />

            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              style={{
                padding: "12px 40px 12px 20px",
                borderRadius: "10px",
                border: "2px solid #e5e7eb",
                fontSize: "14px",
                background: "white",
                minWidth: "150px",
                backgroundImage:
                  'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>\')',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "12px",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
              }}
            >
              {availableUsers.map((user) => (
                <option key={user} value={user}>
                  👤 {user}
                </option>
              ))}
            </select>

            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              style={{
                padding: "12px 40px 12px 20px",
                borderRadius: "10px",
                border: "2px solid #e5e7eb",
                fontSize: "14px",
                background: "white",
                minWidth: "130px",
                backgroundImage:
                  'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>\')',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "12px",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
              }}
            >
              {priorityOptions.map((priority) => (
                <option key={priority} value={priority}>
                  🎯 {priority}
                </option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{
                padding: "12px 40px 12px 20px",
                borderRadius: "10px",
                border: "2px solid #e5e7eb",
                fontSize: "14px",
                background: "white",
                minWidth: "130px",
                backgroundImage:
                  'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>\')',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "12px",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
              }}
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  📊 {status}
                </option>
              ))}
            </select>
          </div>

          {/* Gantt Chart */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              borderRadius: "20px",
              padding: "30px",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
              marginBottom: "30px",
            }}
          >
            <h2
              style={{
                fontSize: "1.8rem",
                fontWeight: "bold",
                marginBottom: "20px",
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              📅 Website Timeline View
            </h2>
            <GanttView
              data={filteredData}
              type="website"
              selectedUser={selectedUser}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default WebsiteTeamView;
